import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox, CheckboxField, CheckboxGroup } from '../catalyst/checkbox'
import { Description, Field, FieldGroup, Fieldset, Label, Legend } from '../catalyst/fieldset'
import { Heading, Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { api } from '../lib/api'
import type { AdminFertilizer, Analysis, Nutrient, ReviewStatus } from '../lib/types'
import { CATEGORY_LABELS } from './fertilizers-page'
import { HistoryList, ReviewBadge, ReviewPanel } from './review'
import { Shell } from './shell'

const NUTRIENTS: Nutrient[] = ['N', 'P2O5', 'K2O', 'CA', 'MG', 'S', 'B', 'CU', 'FE', 'MN', 'MO', 'ZN', 'SI']
const FORMS = ['SOLID', 'LIQUID', 'GAS'] as const
const MODES = ['SOIL', 'FOLIAR', 'FERTIGATION', 'SEED', 'DRY_BLEND_COATING'] as const
const SEGMENTS = ['BLUEBERRY', 'FIELD_CROPS', 'GREENHOUSE', 'NURSERY'] as const
const CERTIFIERS = ['ECOCERT', 'OMRI', 'PRO_CERT'] as const
const ORGANIC = ['CONVENTIONAL', 'ORGANIC_BASED', 'ORGANIC', 'UNKNOWN'] as const
const FIT_BASES = ['SUPPLIER_STATED', 'ASSESSED', 'GENERAL_PURPOSE', 'UNKNOWN'] as const
const ANALYSIS_STATUSES = ['PARSED', 'NEEDS_REVIEW', 'VARIABLE', 'NONE'] as const
const pretty = (v: string) => v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ')

/** What the form edits: everything staff can change, as strings where an input needs one. */
type Draft = Omit<AdminFertilizer, 'id' | 'history' | 'analysis' | 'densityKgPerL'> & { analysis: Record<Nutrient, string>; densityKgPerL: string }

function toDraft(f: AdminFertilizer): Draft {
  const analysis = Object.fromEntries(NUTRIENTS.map((n) => [n, f.analysis[n] === undefined ? '' : String(f.analysis[n])])) as Record<Nutrient, string>
  return { ...f, analysis, densityKgPerL: f.densityKgPerL === null ? '' : String(f.densityKgPerL) }
}

function fromAnalysis(a: Record<Nutrient, string>): Analysis {
  const out: Analysis = {}
  for (const n of NUTRIENTS) if (a[n].trim() !== '') out[n] = Number(a[n])
  return out
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

function CheckGroup<T extends string>({ legend, options, value, onChange }: { legend: string; options: readonly T[]; value: T[]; onChange: (next: T[]) => void }) {
  return (
    <Fieldset>
      <Legend>{legend}</Legend>
      <CheckboxGroup className="mt-2 flex flex-wrap gap-x-6">
        {options.map((o) => (
          <CheckboxField key={o}>
            <Checkbox checked={value.includes(o)} onChange={() => onChange(toggle(value, o))} />
            <Label>{pretty(o)}</Label>
          </CheckboxField>
        ))}
      </CheckboxGroup>
    </Fieldset>
  )
}

function SelectField<T extends string>({ label, value, options, render, onChange }: { label: string; value: T; options: readonly T[]; render?: (v: T) => string; onChange: (v: T) => void }) {
  return (
    <Field>
      <Label>{label}</Label>
      <Select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {render ? render(o) : pretty(o)}
          </option>
        ))}
      </Select>
    </Field>
  )
}

export function FertilizerPage() {
  const { fertilizerId } = useParams()
  const [product, setProduct] = useState<AdminFertilizer | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setProduct(null)
    api
      .get<AdminFertilizer>(`/admin/fertilizers/${fertilizerId}`)
      .then((f) => {
        setProduct(f)
        setDraft(toDraft(f))
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load this product.'))
  }, [fertilizerId])

  async function save(body: Record<string, unknown>) {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const next = await api.patch<AdminFertilizer>(`/admin/fertilizers/${fertilizerId}`, body)
      setProduct(next)
      setDraft(toDraft(next))
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  function saveEdits(event: React.FormEvent) {
    event.preventDefault()
    if (!draft) return
    const density = draft.densityKgPerL.trim()
    void save({
      supplier: draft.supplier,
      brand: draft.brand || null,
      name: draft.name,
      forms: draft.forms,
      applicationModes: draft.applicationModes,
      release: draft.release,
      category: draft.category,
      organicStatus: draft.organicStatus,
      organicCertifiers: draft.organicCertifiers,
      segments: draft.segments,
      fitBasis: draft.fitBasis,
      analysisRaw: draft.analysisRaw || null,
      analysisStatus: draft.analysisStatus,
      analysis: fromAnalysis(draft.analysis),
      densityKgPerL: density === '' ? null : Number(density),
      notes: draft.notes || null,
    })
  }

  if (!product || !draft) {
    return (
      <Shell>
        <Link href="/fertilizers" className="text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
          All fertilizers
        </Link>
        <Text className="mt-6">{error ?? 'Loading…'}</Text>
      </Shell>
    )
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft({ ...draft, [key]: value })

  return (
    <Shell>
      <Link href="/fertilizers" className="text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        All fertilizers
      </Link>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Heading>{product.name}</Heading>
        <ReviewBadge status={product.reviewStatus} />
      </div>
      <Text className="mt-1">
        Compared against the supplier's page: <a className="underline" href={product.sourceUrl} target="_blank" rel="noreferrer">{product.sourceUrl}</a>
      </Text>

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <form onSubmit={saveEdits} className="space-y-8">
          <FieldGroup>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field>
                <Label>Name</Label>
                <Input value={draft.name} onChange={(e) => set('name', e.target.value)} required />
              </Field>
              <Field>
                <Label>Supplier</Label>
                <Input value={draft.supplier} onChange={(e) => set('supplier', e.target.value)} required />
              </Field>
              <Field>
                <Label>Brand</Label>
                <Input value={draft.brand ?? ''} onChange={(e) => set('brand', e.target.value)} />
              </Field>
              <SelectField label="Type" value={draft.category} options={Object.keys(CATEGORY_LABELS) as AdminFertilizer['category'][]} render={(c) => CATEGORY_LABELS[c]} onChange={(v) => set('category', v)} />
              <SelectField label="Release" value={draft.release} options={['STANDARD', 'CONTROLLED'] as const} onChange={(v) => set('release', v)} />
              <SelectField label="Organic" value={draft.organicStatus} options={ORGANIC} onChange={(v) => set('organicStatus', v)} />
            </div>
            <CheckGroup legend="Form" options={FORMS} value={draft.forms} onChange={(v) => set('forms', v)} />
            <CheckGroup legend="How it goes on" options={MODES} value={draft.applicationModes} onChange={(v) => set('applicationModes', v)} />
            <CheckGroup legend="Organic certifiers" options={CERTIFIERS} value={draft.organicCertifiers} onChange={(v) => set('organicCertifiers', v)} />
            <CheckGroup legend="Market segments" options={SEGMENTS} value={draft.segments} onChange={(v) => set('segments', v)} />
            <SelectField label="How segments were decided" value={draft.fitBasis} options={FIT_BASES} onChange={(v) => set('fitBasis', v)} />
          </FieldGroup>

          <Fieldset>
            <Legend>Analysis</Legend>
            <Field className="mt-2">
              <Label>As the supplier states it</Label>
              <Input value={draft.analysisRaw ?? ''} onChange={(e) => set('analysisRaw', e.target.value)} />
              <Description>Kept verbatim. The figures below are read from it; fix them if they do not match.</Description>
            </Field>
            <div className="mt-4 grid grid-cols-3 gap-4 sm:grid-cols-5">
              {NUTRIENTS.map((n) => (
                <Field key={n}>
                  <Label>{n} %</Label>
                  <Input type="number" step="any" min={0} max={100} value={draft.analysis[n]} onChange={(e) => set('analysis', { ...draft.analysis, [n]: e.target.value })} />
                </Field>
              ))}
            </div>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              <SelectField label="Analysis status" value={draft.analysisStatus} options={ANALYSIS_STATUSES} onChange={(v) => set('analysisStatus', v)} />
              <Field>
                <Label>Density (kg/L)</Label>
                <Input type="number" step="any" min={0} value={draft.densityKgPerL} onChange={(e) => set('densityKgPerL', e.target.value)} />
                <Description>Liquids only. Leave empty if unknown; never guess.</Description>
              </Field>
            </div>
          </Fieldset>

          <Field>
            <Label>Notes</Label>
            <Textarea rows={3} value={draft.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
          </Field>

          {error && <Text className="text-red-600 dark:text-red-400">{error}</Text>}
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && <Text className="!mt-0">Saved.{product.reviewStatus === 'unreviewed' ? ' It needs review again.' : ''}</Text>}
          </div>
        </form>

        <div className="space-y-8">
          <ReviewPanel
            key={product.reviewStatus + product.reviewedAt}
            status={product.reviewStatus}
            notes={product.reviewNotes}
            reviewedAt={product.reviewedAt}
            busy={saving}
            onReview={(review: { status: ReviewStatus; notes: string | null }) => void save({ review })}
          />
          <section>
            <Subheading>Source text</Subheading>
            <dl className="mt-2 space-y-1 text-sm/6 text-zinc-600 dark:text-zinc-400">
              {[['Form', product.formRaw], ['Type', product.categoryRaw], ['Organic', product.organicRaw], ['Segments', product.fitBasisRaw]].map(([k, v]) => (
                <div key={k}>
                  <dt className="inline font-medium text-zinc-950 dark:text-white">{k}: </dt>
                  <dd className="inline">{v || '—'}</dd>
                </div>
              ))}
            </dl>
          </section>
          <HistoryList history={product.history} />
        </div>
      </div>
    </Shell>
  )
}
